import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from '@/hooks/useAuth';
import { promptTemplatesService } from '@/services';
import { 
  ArrowLeft, 
  Save, 
  FileText,
  Code,
  Settings as SettingsIcon
} from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface PromptTemplate {
  id?: string;
  name: string;
  type: string;
  templateContent: string;
  variablesSchema: any;
  outputSchema: any;
  isDefault: boolean;
}

const TypeAppelOffresForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<PromptTemplate>({
    name: "",
    type: "analysis_prompt",
    templateContent: "",
    variablesSchema: null,
    outputSchema: null,
    isDefault: false,
  });

  const [variablesSchemaText, setVariablesSchemaText] = useState("");
  const [outputSchemaText, setOutputSchemaText] = useState("");

  useEffect(() => {
    if (isEditMode && id) {
      loadTemplate();
    }
  }, [id, isEditMode]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const data = await promptTemplatesService.getById(id!);

      if (data) {
        setFormData(data);
        setVariablesSchemaText(data.variablesSchema ? JSON.stringify(data.variablesSchema, null, 2) : "");
        setOutputSchemaText(data.outputSchema ? JSON.stringify(data.outputSchema, null, 2) : "");
      }
    } catch (error) {
      console.error('Erreur lors du chargement du template:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger le template",
        variant: "destructive",
      });
      navigate('/types-appel-offres');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PromptTemplate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateAndParseJSON = (jsonString: string, fieldName: string) => {
    if (!jsonString.trim()) return null;
    
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      toast({
        title: "Erreur JSON",
        description: `Format JSON invalide dans le champ ${fieldName}`,
        variant: "destructive",
      });
      return false;
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Validation",
        description: "Le nom est requis",
        variant: "destructive",
      });
      return;
    }

    if (!formData.templateContent.trim()) {
      toast({
        title: "Validation",
        description: "Le contenu du template est requis",
        variant: "destructive",
      });
      return;
    }

    // Validate JSON schemas
    const variablesSchema = validateAndParseJSON(variablesSchemaText, "Variables Schema");
    if (variablesSchema === false) return;

    const outputSchema = validateAndParseJSON(outputSchemaText, "Output Schema");
    if (outputSchema === false) return;

    try {
      setLoading(true);

      if (!user) throw new Error("Utilisateur non connecté");

      const templateData = {
        name: formData.name,
        type: formData.type,
        templateContent: formData.templateContent,
        variablesSchema: variablesSchema,
        outputSchema: outputSchema,
        isDefault: formData.isDefault,
      };

      if (isEditMode && id) {
        await promptTemplatesService.update(id, templateData);
        toast({
          title: "Succès",
          description: "Template modifié avec succès",
        });
      } else {
        await promptTemplatesService.create(templateData);
        toast({
          title: "Succès",
          description: "Template créé avec succès",
        });
      }

      navigate('/types-appel-offres');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder le template",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/types-appel-offres')}
              className="mr-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <SettingsIcon className="h-8 w-8 mr-3 text-primary" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                {isEditMode ? 'Modifier le type' : 'Nouveau type d\'appel d\'offres'}
              </h1>
              <p className="text-muted-foreground">
                {isEditMode ? 'Modifiez le template de prompt' : 'Créez un nouveau template de prompt pour l\'analyse'}
              </p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? "Sauvegarde..." : "Sauvegarder"}
          </Button>
        </div>

        <div className="grid gap-6">
          {/* Informations de base */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Informations générales
              </CardTitle>
              <CardDescription>
                Définissez les propriétés de base de votre template
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom du template *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Ex: Analyse CV Candidats"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => handleInputChange('type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="analysis_prompt">Analysis Prompt</SelectItem>
                      <SelectItem value="output_format">Output Format</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="isDefault"
                  checked={formData.isDefault}
                  onCheckedChange={(checked) => handleInputChange('isDefault', checked)}
                />
                <Label htmlFor="isDefault">Template par défaut</Label>
              </div>
            </CardContent>
          </Card>

          {/* Contenu du template */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Code className="h-5 w-5 mr-2" />
                Contenu du prompt
              </CardTitle>
              <CardDescription>
                Définissez le prompt qui sera utilisé pour l'analyse
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="templateContent">Prompt template *</Label>
                <Textarea
                  id="templateContent"
                  value={formData.templateContent}
                  onChange={(e) => handleInputChange('templateContent', e.target.value)}
                  placeholder="Écrivez votre prompt ici..."
                  rows={10}
                  className="font-mono text-sm"
                />
                <div className="text-sm text-muted-foreground">
                  Utilisez des variables avec la syntaxe {"{variable_name}"} si nécessaire
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Schémas JSON */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Code className="h-5 w-5 mr-2" />
                Configuration avancée
              </CardTitle>
              <CardDescription>
                Définissez les schémas de variables et de sortie (optionnel)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="variables_schema">Schéma des variables (JSON)</Label>
                <Textarea
                  id="variables_schema"
                  value={variablesSchemaText}
                  onChange={(e) => setVariablesSchemaText(e.target.value)}
                  placeholder='{"variable1": {"type": "string", "description": "..."}, ...}'
                  rows={6}
                  className="font-mono text-sm"
                />
                <div className="text-sm text-muted-foreground">
                  Définit les variables attendues dans le template (format JSON)
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="output_schema">Schéma de sortie (JSON)</Label>
                <Textarea
                  id="output_schema"
                  value={outputSchemaText}
                  onChange={(e) => setOutputSchemaText(e.target.value)}
                  placeholder='{"competences": {"type": "array"}, "score": {"type": "number"}, ...}'
                  rows={6}
                  className="font-mono text-sm"
                />
                <div className="text-sm text-muted-foreground">
                  Définit la structure attendue de la réponse (format JSON)
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default TypeAppelOffresForm;
